import type { Cable, Channel } from 'actioncable'
import debounce from 'lodash/debounce'

import ErrorBoundary from '../containers/error_boundary'

type SubscriptionCreateType = {
  update: () => void
  install: () => void
  uninstall: () => void
  documentIsActive: () => boolean
  appear: () => void
  away: () => void
}

type ChannelType = SubscriptionCreateType & Partial<Channel>

const WINDOW_EVENTS = ['pageshow', 'focus', 'blur', 'resume']

export default function createSubscription(consumer: Cable, options = {}) {
  const params: any = {
    channel: 'AppearanceChannel',
    ...options
  }
  consumer.subscriptions.create<SubscriptionCreateType>(params, {
    // Called once when the subscription is created.
    initialized() {
      this.update = this.update.bind(this)
      this.appear = debounce(this.appear.bind(this), 100, { leading: true })
      this.away = debounce(this.away.bind(this), 60000)
    },

    // Called when the subscription is ready for use on the server.
    connected() {
      this.install()
      this.update()
    },

    // Called when the WebSocket connection is closed.
    disconnected() {
      this.uninstall()
    },

    // Called when the subscription is rejected by the server.
    rejected() {
      ErrorBoundary.logErrorToMyService(
        new Error('Rejected websocket connection for: AppearanceChannel'),
      )
      this.uninstall()
    },

    received(data: any) {
      if (data === 'ping') {
        this.perform && this.perform('pong', {})
      }

      if (data === 'here') {
        if (this.documentIsActive()) {
          this.appear()
        }
      }
    },

    update() {
      // eslint-disable-next-line
      // @ts-ignore
      this.away.cancel()
      this.documentIsActive() ? this.appear() : this.away()
    },

    appear() {
      this.perform && this.perform('appear', {})
    },

    away() {
      this.perform && this.perform('away', {})
    },

    install() {
      WINDOW_EVENTS.forEach((type) => {
        window.addEventListener(type, this.update, { capture: true })
      })

      // NOTE: visibilitychange should be handled on window, but a bug in pre iOS14 Safari prevents this event from bubbling
      document.addEventListener('visibilitychange', this.update)
      document.addEventListener('turbolinks:load', this.update)
    },

    uninstall() {
      WINDOW_EVENTS.forEach((type) => {
        window.removeEventListener(type, this.update)
      })

      // NOTE: visibilitychange should be handled on window, but a bug in pre iOS14 Safari prevents this event from bubbling
      document.removeEventListener('visibilitychange', this.update)
      document.removeEventListener('turbolinks:load', this.update)
    },

    documentIsActive() {
      return document.hasFocus()
    },
  } as ChannelType)
}
